import React, { useState, useEffect } from 'react';
import DayPicker, { DateUtils } from 'react-day-picker';
import 'react-day-picker/lib/style.css';

import {
  createFormBag,
  FormBag,
  FormData,
  FormErrors,
  validateFormBag,
  Field
} from 'react-formage';

import { StatusGuard } from 'components/ui/status-guard';
import { connect } from 'containers/store';
import { Site } from 'generated/mos/structure';
import { structuresActionCreators } from 'domains/structures/actions';
import { DataStatus as StructuresDataStatus } from 'domains/structures/store';
import { Structures } from 'domains/structures/entities';
import { actionCreators, GettyActionCreators } from 'domains/org/getty/actions';
import { GettyStore, GettyAppState } from 'domains/org/getty/store';
import { pick } from 'helpers/core';
import { tourSchema } from '../../../../schemas/tour';

import { statusSelector } from 'helpers/status';

import { Select, Option } from 'components/ui/select';
import { Button } from 'components/ui/button';
import { QueryProps } from '../use-query';
import { QueryPanelContainer } from './styled-container';

const initialListOptions: readonly Option<string | null>[] = [{ value: '', label: '' }];
const bag: FormBag<QueryProps> = createFormBag({
  siteRef: null,
  tourRef: null,
  fromDate: new Date(),
  toDate: new Date(),
  metadata: { siteName: '', tourName: ''}
});

type ConnectedProps = {
  analytics: GettyStore['getty']['analytics'];
  readonly structures: StructuresDataStatus<Structures>;
  readonly tours: GettyAppState['tours'];
};

type DirectProps = {
  sendQuery: (queryData: any) => void;
};

type ActionProps = Pick<GettyActionCreators, 'gettyListToursRequest'> &
  Pick<typeof structuresActionCreators, 'structuresLoadRequest'>;

type Props = ConnectedProps & ActionProps & DirectProps;


const today = new Date();

const QueryPanelView: React.FC<Props> = props => {
  const structureData = statusSelector.data(props.structures);
  const tourData = statusSelector.data(props.tours);

  const [formBag, setFormBag] = useState(bag);
  const [siteOptions, setSiteOptions] = useState(initialListOptions);
  const [tourOptions, setTourOptions] = useState(initialListOptions);
  const [dateRange, setDateRange] = useState<{ to: Date; from: Date }>({
    from: today,
    to: today
  });

  useEffect(() => {
    statusSelector.shouldLoad(props.structures.status) && props.structuresLoadRequest();
  });

  useEffect(() => {
    statusSelector.shouldLoad(props.tours.status) &&
      props.gettyListToursRequest({
        sortOrder: 'SORT_DEFAULT',
        type: 'mos.org.getty.audio_access.ListToursRequest',
        filter: undefined
      });
  });

  useEffect(() => {
    formBag && validateForm(formBag.values);
  }, [tourOptions, siteOptions, formBag]);

  useEffect(() => {
    if (!structureData) return;

    const sites: readonly Option<string>[] = structureData.sites.map((site: Site.Entity) => {
      if (!site.ref) throw Error('Siteref not found');
      const value = (site.ref && site.ref.id) || 'unknown';
      const obj: Option<string> = {value, label: site.name};
      return obj;
    });

    setSiteOptions(sites);
  }, [structureData]);

  const findToursForSite = (siteRef: string): readonly Option<string | null>[] => {
    if (!tourData) return initialListOptions;
    const tours = tourData
      .filter(tour => tour.siteRef && tour.siteRef.id === siteRef)
      .map(tour => {
        const value = tour.ref && tour.ref.id || 'unknown';
        const label= String(tourSchema.name.read(tour.name));
        const obj: Option<string> = {label, value};
        return obj;
      });
    return [{ value: null, label: 'All' }, ...tours];
  };

  const onFormChange = (bag: FormBag<QueryProps>) => {

    
    // if the change was a new site being selected
    // then clear the currently selected tour
    if (formBag.values.siteRef !== bag.values.siteRef) {
      bag.values.tourRef = null;
      bag.values.siteRef && setTourOptions(findToursForSite(bag.values.siteRef));
    }
    
    const tourOption = tourOptions.find(option => option.value === bag.values.tourRef);
    const tourName = tourOption ? String(tourOption.label) : '';
    
    const siteOption = siteOptions.find(option => option.value === bag.values.siteRef);
    const siteName = siteOption ? String(siteOption.label) : '';
    
    bag.values.metadata = { tourName, siteName};  
    
    setFormBag(bag);
  };

  const validateForm = (values: QueryProps): FormErrors<QueryProps> => {
    const errors: FormErrors<QueryProps> = {};
    const required: ReadonlyArray<keyof QueryProps> = ['siteRef'];
    for (var key of required) {
      if (!values[key]) {
        errors[key] = 'Value is required!';
      }
    }
    return errors;
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const bag = validateFormBag(formBag, validateForm);
    setFormBag(bag);

    const query = {
      siteRef: bag.values.siteRef,
      tourRef: bag.values.tourRef,
      fromDate: dateRange.from,
      toDate: dateRange.to,
      metadata: bag.values.metadata
    };

    bag.valid && props.sendQuery(query);
  };

  return (
    <StatusGuard status={props.tours}>
      <StatusGuard status={props.structures}>
        <QueryPanelContainer>
          <form noValidate onSubmit={onSubmit} >
            <FormData
              bag={formBag}
              validateOnChange={true}
              onUpdate={event => {
                onFormChange(event.bag);
              }}
              validate={() => validateForm(formBag.values)}
            >
              <Field<QueryProps, 'toDate'>
                name="toDate"
                render={props => (
                  <DayPicker
                    className="dayPicker"
                    selectedDays={[dateRange.from, { from: dateRange.from, to: dateRange.to }]}
                    onDayClick={(d: Date) => {
                    setDateRange(DateUtils.addDayToRange(d, dateRange) as typeof dateRange);
                    props.change(d);
                  }}
                  />
                  )}
                />
              <Select<QueryProps, 'siteRef'>
                label="Site"
                field="siteRef"
                options={siteOptions}
                isDisabled={!structureData}
                />

              <Select<QueryProps, 'tourRef'>
                label="Tour"
                field="tourRef"
                placeholder={
                    (!formBag.values.siteRef && 'Select a site first!') ||
                    (!tourData && 'Loading Data') ||
                    'Select...'
                  }
                options={tourOptions}
                isDisabled={formBag.values.siteRef === null}
                isSearchable={true}
                />
              <Button type="submit" isDisabled={!formBag.valid} onClick={onSubmit}>
                Submit
              </Button>
            </FormData>
          </form>
        </QueryPanelContainer>
      </StatusGuard>
    </StatusGuard>
  );
};

export const QueryForm = connect<
  ConnectedProps,
  ActionProps,
  DirectProps,
  GettyStore,
  GettyActionCreators
>(
  store => ({
    analytics: store.getty.analytics,
    ...pick(store.structures, 'structures'),
    ...pick(store.getty, 'tours')
  }),
  {
    ...pick(actionCreators, 'gettyListToursRequest'),
    ...pick(structuresActionCreators, 'structuresLoadRequest')
  }
)(QueryPanelView);
